import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setActiveFacility } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FacilityContext = createContext(null);
export const useFacility = () => useContext(FacilityContext);

const STORAGE_KEY = "span_active_facility";

export function FacilityProvider({ children }) {
  const { auth } = useAuth();
  const facilities = (auth && auth.facilities) || [];
  const [activeId, setActiveId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [activeModules, setActiveModules] = useState([]);
  const [loadingCtx, setLoadingCtx] = useState(false);

  const loadFacilityContext = useCallback(async (facilityId) => {
    if (!facilityId) return;
    setLoadingCtx(true);
    setActiveFacility(facilityId);
    try {
      const { data } = await api.get(`/context/facility/${facilityId}`);
      setPermissions(data.permissions || []);
      setActiveModules(data.active_modules || []);
    } catch {
      setPermissions([]);
      setActiveModules([]);
    } finally {
      setLoadingCtx(false);
    }
  }, []);

  useEffect(() => {
    if (!auth || auth.is_platform_admin) { setActiveFacility(null); return; }
    if (facilities.length === 0) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    const valid = facilities.find((f) => f.id === stored);
    const chosen = valid ? valid.id : facilities[0].id;
    setActiveId(chosen);
    setActiveFacility(chosen);
    localStorage.setItem(STORAGE_KEY, chosen);
    loadFacilityContext(chosen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const switchFacility = async (facilityId) => {
    setActiveId(facilityId);
    setActiveFacility(facilityId);
    localStorage.setItem(STORAGE_KEY, facilityId);
    await loadFacilityContext(facilityId);
  };

  const has = (perm) => permissions.includes("*") || permissions.includes(perm);
  const activeFacility = facilities.find((f) => f.id === activeId) || null;

  return (
    <FacilityContext.Provider value={{ facilities, activeFacility, activeId, permissions, activeModules, loadingCtx, switchFacility, has, refresh: () => loadFacilityContext(activeId) }}>
      {children}
    </FacilityContext.Provider>
  );
}
